import { Injectable } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { ObjectiveStatus, ObjectiveStatusMetadata } from "@common/ADAPT.Common.Model/organisation/objective-status";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { UserService } from "@common/user/user.service";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityHandler } from "@org-common/lib/activities/activity-handler";
import { NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { ObjectivesService } from "@org-common/lib/objectives/objectives.service";
import moment from "moment";
import { lastValueFrom } from "rxjs";
import { filter, map } from "rxjs/operators";
import { IActivityGroup } from "../activities-metadata";

@Injectable()
export class ObjectiveActivityHandler extends ActivityHandler<Objective> {
    public readonly entityType =  Objective;
    public readonly activityTypes = [NonCulturalActivityType.Objectives];

    constructor(
        private objectivesService: ObjectivesService,
        private rxjsBreezeService: RxjsBreezeService,
        private userService: UserService,
    ) {
        super();
    }

    public get shouldUpdate$() {
        return this.rxjsBreezeService.entityTypeChanged(Objective).pipe(
            filter((objective) => objective.assigneePersonId === this.userService.currentPerson?.personId),
            map(() => [NonCulturalActivityType.Objectives]),
        );
    }

    public async getGroupedActivities(personId: number, startDate?: Date, endDate?: Date, excludeCompleted?: boolean) {
        const group = ActivitiesService.generateActivitiesGroupObject();

        const predicate = new MethodologyPredicate<Objective>("assigneePersonId", "==", personId);

        if (excludeCompleted) {
            predicate.and(new MethodologyPredicate<Objective>("status", "!=", ObjectiveStatus.Closed));
        }

        if (startDate) {
            predicate.and(new MethodologyPredicate<Objective>("dueDate", ">=", startDate));
        }

        if (endDate) {
            predicate.and(new MethodologyPredicate<Objective>("dueDate", "<=", endDate));
        }

        const allObjectives = (await lastValueFrom(this.objectivesService.getObjectivesByPredicate(predicate)))
            .sort(SortUtilities.getSortByFieldFunction("dueDate"))
            // exclude objectives within inactive teams
            .filter((o) => !o.team || o.team.isActive());

        const objectiveGroups = this.objectivesService.groupObjectivesByTeam(allObjectives);
        const selectedObjectives = objectiveGroups
            // only take 3 objectives per group per status
            .flatMap(({ objectives }) => {
                const [overdue, upcoming] = ArrayUtilities.partition(objectives, (o) => getObjectiveStatus(o) === CulturalActivityStatus.Overdue);
                return [...overdue.slice(0, 3), ...upcoming.slice(0, 3)];
            });

        for (const objective of selectedObjectives) {
            const status = getObjectiveStatus(objective);
            group[status].push(objective);
        }

        group.allItems = allObjectives;
        group.totalCount = allObjectives.length;
        group.totalCountByStatus = allObjectives.reduce((counts, objective) => {
            counts[getObjectiveStatus(objective)] += 1;
            return counts;
        }, ActivitiesService.generateActivitiesGroupedByStatusCountObject());

        return { [NonCulturalActivityType.Objectives]: group };

        function getObjectiveStatus(objective: Objective) {
            const isOverdue = moment().startOf("day").isSameOrAfter(objective.dueDate);
            return isOverdue ? CulturalActivityStatus.Overdue : CulturalActivityStatus.Upcoming;
        }
    }

    public groupActivitiesByStatus(group: IActivityGroup<Objective>) {
        return {
            [CulturalActivityStatus.Overdue]: this.objectivesService.groupObjectivesByTeam(group[CulturalActivityStatus.Overdue]),
            [CulturalActivityStatus.Upcoming]: this.objectivesService.groupObjectivesByTeam(group[CulturalActivityStatus.Upcoming]),
        };
    }

    public async getCalendarEventForActivity(activity: Objective) {
        let title = activity.title;
        const completed = activity.status === ObjectiveStatus.Closed || activity.status === ObjectiveStatus.Complete;
        if (completed) {
            const statusMeta = ObjectiveStatusMetadata.ByStatus[activity.status];
            title = `(${statusMeta.name}) ${title}`;
        }

        return {
            title,
            startDate: activity.dueDate,
            endDate: undefined,
            completed,
        };
    }
}
